import { memo, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Ship, Search, Star, Bell } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { ALERT_BADGE_EVENT_NAME, clearAlertBadge, getAlertBadgeCount } from '@/hooks/useAlertTrigger';
import { useLanguage } from '@/contexts/LanguageContext';

type Tab = 'home' | 'search' | 'favorites' | 'alerts';

interface BottomNavProps {
  onSearchFocus?: () => void;
  onFavoritesOpen?: () => void;
}

const TAB_TO: Record<Tab, string> = {
  home: '/',
  search: '/sok',
  favorites: '/mine-ferger',
  alerts: '/varsler',
};

export const BottomNav = memo((_props: BottomNavProps) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { t } = useLanguage();
  const [unseenCount, setUnseenCount] = useState<number>(() => getAlertBadgeCount());

  useEffect(() => {
    const sync = () => setUnseenCount(getAlertBadgeCount());
    window.addEventListener(ALERT_BADGE_EVENT_NAME, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(ALERT_BADGE_EVENT_NAME, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  if (!isMobile) return null;

  const isHome = location.pathname === '/' || location.pathname === '/ferge';
  const isSearch = location.pathname === '/sok';
  const isMyFerries = location.pathname === '/mine-ferger';
  const isAlerts = location.pathname === '/varsler';

  const tabs: { id: Tab; icon: typeof Ship; label: string }[] = [
    { id: 'home', icon: Ship, label: t('home') },
    { id: 'search', icon: Search, label: t('nav_search') },
    { id: 'favorites', icon: Star, label: t('nav_my_ferries') },
    { id: 'alerts', icon: Bell, label: t('nav_alerts') },
  ];

  return (
    <nav
      aria-label="Hovednavigasjon"
      className="fixed bottom-0 left-0 right-0 z-50 bg-ferry-card border-t border-ferry-border-subtle safe-area-bottom md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="flex items-center justify-around h-14">
        {tabs.map(({ id, icon: Icon, label }) => {
          const isActive =
            (id === 'home' && isHome) ||
            (id === 'search' && isSearch) ||
            (id === 'favorites' && isMyFerries) ||
            (id === 'alerts' && isAlerts);
          const showBadge = id === 'alerts' && unseenCount > 0 && !isAlerts;
          return (
            <Link
              key={id}
              to={TAB_TO[id]}
              onClick={() => {
                if (id === 'alerts') clearAlertBadge();
              }}
              aria-current={isActive ? 'page' : undefined}
              aria-label={label}
              className={cn(
                'tap-feedback flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors relative',
                isActive ? 'text-ferry-accent' : 'text-ferry-text-muted',
              )}
            >
              <span className="relative">
                <Icon className="h-5 w-5" aria-hidden="true" />
                {showBadge && (
                  <span className="absolute -top-1 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-semibold flex items-center justify-center leading-none">
                    {unseenCount > 9 ? '9+' : unseenCount}
                  </span>
                )}
              </span>
              <span className="text-[10px] font-medium">{label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
});

BottomNav.displayName = 'BottomNav';
